import type { GutScore, Meal, Symptom } from './types';
import { calculateGutBaseline, isFlareSymptom, isStoolOnlyEntry } from './correlationEngine';

const WEIGHTS = { flareFree: 0.4, severity: 0.3, stool: 0.2, wellbeing: 0.1 } as const;

export function gutBand(score: number): GutScore['band'] {
  if (score < 30) return 'FLARING';
  if (score < 80) return 'SENSITIVE';
  return 'STABLE';
}

function dayKey(ms: number): string {
  const d = new Date(ms);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Wellbeing is normally 1-5; legacy values above 5 are on a 0-10 scale. */
function normaliseWellbeing(w: number): number {
  const v = w > 5 ? w / 10 : w / 5;
  return Math.min(1, Math.max(0, v));
}

function severityOf(s: Symptom): number {
  const vals = [s.bloating_score || 0, s.soreness_score || 0, ...Object.values(s.symptom_scores || {})];
  return Math.max(0, ...vals);
}

const mean = (xs: number[]): number | null => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export function computeGutScore(meals: Meal[], symptoms: Symptom[], endMs: number, days = 7): GutScore | null {
  const end = new Date(endMs);
  const windowDays = new Set<string>();
  for (let i = 0; i < days; i++) {
    windowDays.add(dayKey(new Date(end.getFullYear(), end.getMonth(), end.getDate() - i, 12).getTime()));
  }
  const inWindow = symptoms.filter((s) => windowDays.has(dayKey(new Date(s.timestamp_iso).getTime())));
  const byDay = new Map<string, Symptom[]>();
  for (const s of inWindow) {
    const k = dayKey(new Date(s.timestamp_iso).getTime());
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(s);
  }
  const loggedDays = byDay.size;
  if (loggedDays < 3) return null;

  const baseline = calculateGutBaseline(meals, symptoms);
  let flareDays = 0;
  const daySeverities: number[] = [];
  for (const list of byDay.values()) {
    if (list.some((s) => isFlareSymptom(s, baseline))) flareDays++;
    const rated = list.filter((s) => !isStoolOnlyEntry(s));
    if (rated.length) daySeverities.push(Math.max(...rated.map(severityOf)));
  }

  const stools = inWindow.filter((s) => s.stool_type !== null && s.stool_type !== undefined).map((s) => s.stool_type as number);
  const wellbeings = inWindow
    .filter((s) => !isStoolOnlyEntry(s) && typeof s.overall_wellbeing === 'number')
    .map((s) => normaliseWellbeing(s.overall_wellbeing));

  const sevMean = mean(daySeverities);
  const components: GutScore['components'] = {
    flareFree: (loggedDays - flareDays) / loggedDays,
    severity: sevMean === null ? null : Math.max(0, 1 - sevMean / 10),
    stool: stools.length ? stools.filter((t) => t === 3 || t === 4).length / stools.length : null,
    wellbeing: mean(wellbeings),
  };

  let sum = 0;
  let wsum = 0;
  (Object.keys(WEIGHTS) as Array<keyof typeof WEIGHTS>).forEach((k) => {
    const c = components[k];
    if (c !== null) {
      sum += WEIGHTS[k] * c;
      wsum += WEIGHTS[k];
    }
  });
  const score = Math.round((100 * sum) / wsum);

  const result: GutScore = {
    score,
    band: gutBand(score),
    loggedDays,
    flareDays,
    stableDays: loggedDays - flareDays,
    components,
    reason: '',
  };

  const counts = new Map<number, number>();
  stools.forEach((t) => counts.set(t, (counts.get(t) || 0) + 1));
  const modeStool = [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0] - b[0])[0]?.[0];
  const parts: string[] = [];
  if (modeStool !== undefined) parts.push(`stools mostly type ${modeStool}`);
  const wbMean = mean(wellbeings);
  if (wbMean !== null) parts.push(`wellbeing ${Math.round(wbMean * 5)}/5`);
  result.reason = parts.join(' · ');
  return result;
}

export function gutScoreReason(current: GutScore, previous: GutScore | null): string {
  const parts: string[] = [];
  if (previous) {
    const delta = current.score - previous.score;
    parts.push(
      delta === 0 ? 'no change vs last week' : `${delta < 0 ? '↓' : '↑'} ${Math.abs(delta)} vs last week`
    );
  }
  if (current.reason) parts.push(current.reason);
  return parts.join(' · ');
}
